import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Lock } from 'lucide-react'
import {
  type IncomingLikesResponse,
  type OutgoingLikeView,
  fetchIncomingLikes,
  fetchOutgoingLikes,
  reportPaywallShown,
} from '../lib/api'
import { useTelegramBackButton } from '../lib/telegram'
import Skeleton from '../components/Skeleton'

type Tab = 'incoming' | 'outgoing'

type IncomingState =
  | { status: 'loading' }
  | { status: 'ready'; data: IncomingLikesResponse }
  | { status: 'error' }

type OutgoingState =
  | { status: 'loading' }
  | { status: 'ready'; items: OutgoingLikeView[] }
  | { status: 'error' }

/**
 * "See Who Likes You" + "My Likes" — the closing item of the Premium phase
 * (docs/monetization-roadmap.md "Kejar Premium Feature" #4).
 *
 * The "Yang suka kamu" tab shows the true count and a blurred photo per
 * entry for EVERY caller — so a free user knows someone is waiting, which
 * is the honest part of this paywall (see the Read Receipts copy
 * correction this phase already went through: never promise more than the
 * mechanism delivers). Only a Premium+ caller (`can_reveal`) can tap a
 * tile; that navigates to `/swipe?reveal=<id>`, which force-injects that
 * person at the front of the swipe deck — identity opens only by the card
 * showing up there, exactly like Crush, never through this list itself.
 * A free caller tapping the same tile goes to the Premium paywall instead.
 *
 * Release rule from the roadmap: an empty incoming list must never carry an
 * upgrade CTA — with a ~1.5% match rate, "nobody likes you yet" is a normal
 * state, not an edge case, and selling against an empty list would burn
 * trust faster than not having the feature at all.
 */
export default function LikesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('incoming')
  const [incoming, setIncoming] = useState<IncomingState>({ status: 'loading' })
  const [outgoing, setOutgoing] = useState<OutgoingState>({ status: 'loading' })

  useEffect(() => {
    fetchIncomingLikes()
      .then((data) => setIncoming({ status: 'ready', data }))
      .catch(() => setIncoming({ status: 'error' }))
  }, [])

  // Outgoing is fetched lazily, once, the first time that tab is opened —
  // most sessions land here for the incoming tab, no reason to always pay
  // for both requests up front.
  useEffect(() => {
    if (tab !== 'outgoing' || outgoing.status !== 'loading') return
    fetchOutgoingLikes()
      .then((items) => setOutgoing({ status: 'ready', items }))
      .catch(() => setOutgoing({ status: 'error' }))
  }, [tab, outgoing.status])

  useTelegramBackButton(() => navigate('/swipe'))

  function handleTileTap(canReveal: boolean, userID: number | undefined) {
    if (canReveal && userID !== undefined) {
      navigate(`/swipe?reveal=${userID}`)
    } else {
      reportPaywallShown('likes_you')
      navigate('/buy-vip?tier=premium&reason=likes_you')
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-tg-bg">
      <div className="flex items-center gap-3 px-4 py-4 border-b border-tg-hint/20">
        <button onClick={() => navigate('/swipe')} className="text-tg-hint text-sm">
          ← {t('common.back')}
        </button>
        <h1 className="text-lg font-semibold text-tg-text">{t('likes.title')}</h1>
      </div>

      <div className="flex border-b border-tg-hint/20">
        <button
          onClick={() => setTab('incoming')}
          className={`flex-1 py-3 text-sm font-medium ${
            tab === 'incoming' ? 'text-primary border-b-2 border-primary' : 'text-tg-hint'
          }`}
        >
          {t('likes.tabIncoming')}
        </button>
        <button
          onClick={() => setTab('outgoing')}
          className={`flex-1 py-3 text-sm font-medium ${
            tab === 'outgoing' ? 'text-primary border-b-2 border-primary' : 'text-tg-hint'
          }`}
        >
          {t('likes.tabOutgoing')}
        </button>
      </div>

      {tab === 'incoming' && (
        <div className="flex-1 p-4">
          {incoming.status === 'loading' && (
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-xl" />
              ))}
            </div>
          )}

          {incoming.status === 'error' && (
            <p className="mt-8 text-center text-sm text-tg-hint">{t('common.genericError')}</p>
          )}

          {incoming.status === 'ready' && incoming.data.count === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-8 pt-16 text-center">
              <span className="text-4xl">💗</span>
              <p className="font-medium text-tg-text">{t('likes.emptyIncomingTitle')}</p>
              <p className="text-sm text-tg-hint">{t('likes.emptyIncomingMessage')}</p>
            </div>
          )}

          {incoming.status === 'ready' && incoming.data.count > 0 && (
            <>
              <p className="mb-3 text-sm font-medium text-tg-text">
                {t('likes.incomingCount', { count: incoming.data.count })}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {incoming.data.items.map((item, i) => (
                  <button
                    key={item.user_id ?? i}
                    onClick={() => handleTileTap(incoming.data.can_reveal, item.user_id)}
                    className="relative aspect-square overflow-hidden rounded-xl bg-tg-secondary-bg"
                  >
                    {item.photo_url ? (
                      <img
                        src={item.photo_url}
                        alt=""
                        className="h-full w-full object-cover"
                        aria-hidden="true"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-primary/30 to-tg-secondary-bg" />
                    )}
                    {!incoming.data.can_reveal && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                        <Lock className="h-5 w-5 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {!incoming.data.can_reveal && (
                <button
                  onClick={() => {
                    reportPaywallShown('likes_you')
                    navigate('/buy-vip?tier=premium&reason=likes_you')
                  }}
                  className="mt-4 w-full rounded-xl bg-primary px-4 py-3 text-sm font-medium text-white"
                >
                  {t('likes.unlockCta')}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'outgoing' && (
        <div className="flex-1 p-4">
          {outgoing.status === 'loading' && (
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-xl" />
              ))}
            </div>
          )}

          {outgoing.status === 'error' && (
            <p className="mt-8 text-center text-sm text-tg-hint">{t('common.genericError')}</p>
          )}

          {outgoing.status === 'ready' && outgoing.items.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-8 pt-16 text-center">
              <span className="text-4xl">👋</span>
              <p className="font-medium text-tg-text">{t('likes.emptyOutgoingTitle')}</p>
              <p className="text-sm text-tg-hint">{t('likes.emptyOutgoingMessage')}</p>
            </div>
          )}

          {outgoing.status === 'ready' && outgoing.items.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {outgoing.items.map((item) => (
                <div key={item.user_id} className="flex flex-col gap-1">
                  <div className="aspect-square overflow-hidden rounded-xl bg-tg-secondary-bg">
                    {item.photo_url ? (
                      <img src={item.photo_url} alt={item.display_name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-lg font-semibold text-primary">
                        {item.display_name[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <p className="truncate text-xs text-tg-text">{item.display_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
